import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersAPI, importAPI, domainsAPI, smtpAPI, envAPI } from '../services/api';
import useAuthStore from '../stores/authStore';
import {
  Plus,
  X,
  Edit2,
  Trash2,
  Shield,
  UserPlus,
  Search,
  Globe,
  AlertCircle,
  Check,
  Mail,
  Send,
  RefreshCw,
  Key,
  Lock,
  Server,
  HelpCircle,
  CheckCircle2,
  Eye,
  EyeOff,
} from 'lucide-react';

const TIMEZONES = [
  { value: 'Asia/Kolkata', label: 'India (Kolkata)' },
  { value: 'America/New_York', label: 'US Eastern (New York)' },
  { value: 'America/Chicago', label: 'US Central (Chicago)' },
  { value: 'America/Denver', label: 'US Mountain (Denver)' },
  { value: 'America/Los_Angeles', label: 'US Pacific (Los Angeles)' },
  { value: 'Europe/London', label: 'UK (London)' },
  { value: 'Europe/Paris', label: 'Europe (Paris)' },
  { value: 'Asia/Dubai', label: 'UAE (Dubai)' },
  { value: 'Asia/Singapore', label: 'Singapore' },
  { value: 'Australia/Sydney', label: 'Australia (Sydney)' },
  { value: 'UTC', label: 'UTC' },
];

const EMPTY_FORM = {
  name: '',
  email: '',
  username: '',
  password: '',
  parent_id: '',
  roles: ['Member'],
  status: true,
  timezone: 'Asia/Kolkata',
};

const UserProvisioning = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuthStore();
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState({
    page: 1,
    size: 10,
    total_records: 0,
    total_pages: 1
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [showDomainModal, setShowDomainModal] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [newDomainInput, setNewDomainInput] = useState('');
  const [domainLoading, setDomainLoading] = useState(false);
  const [domainError, setDomainError] = useState('');
  const [domainSuccess, setDomainSuccess] = useState('');

  const fetchAllowedDomains = async () => {
    setDomainLoading(true);
    setDomainError('');
    try {
      const res = await domainsAPI.list();
      if (res?.data?.data) {
        setAllowedDomains(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch allowed domains:', err);
      setDomainError(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to fetch allowed domains');
    } finally {
      setDomainLoading(false);
    }
  };

  const openDomainModal = async () => {
    setDomainError('');
    setDomainSuccess('');
    setNewDomainInput('');
    setShowDomainModal(true);
    await fetchAllowedDomains();
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomainInput.trim()) return;
    setDomainLoading(true);
    setDomainError('');
    setDomainSuccess('');
    try {
      const res = await domainsAPI.add(newDomainInput.trim());
      setDomainSuccess(res?.data?.message || 'Domain added successfully');
      setNewDomainInput('');
      await fetchAllowedDomains();
    } catch (err) {
      console.error('Error adding domain:', err);
      setDomainError(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to add domain');
    } finally {
      setDomainLoading(false);
    }
  };

  const handleDeleteDomain = async (domainId, domainName) => {
    if (!window.confirm(`Are you sure you want to remove @${domainName} from allowed domains?`)) return;
    setDomainLoading(true);
    setDomainError('');
    setDomainSuccess('');
    try {
      const res = await domainsAPI.delete(domainId);
      setDomainSuccess(res?.data?.message || 'Domain removed successfully');
      await fetchAllowedDomains();
    } catch (err) {
      console.error('Error deleting domain:', err);
      setDomainError(err?.response?.data?.message || err?.response?.data?.detail || 'Failed to remove domain');
    } finally {
      setDomainLoading(false);
    }
  };

  // ── SMTP Settings State & Handlers ─────────────────────────────────────────
  const [showSmtpModal, setShowSmtpModal] = useState(false);
  const [smtpTab, setSmtpTab] = useState('settings'); // 'settings' | 'queue'
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpError, setSmtpError] = useState('');
  const [smtpSuccess, setSmtpSuccess] = useState('');
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [showSmtpHelp, setShowSmtpHelp] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [emailQueue, setEmailQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueRetrying, setQueueRetrying] = useState(false);

  const [smtpForm, setSmtpForm] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from: '',
    smtp_encryption: 'TLS',
    has_password: false,
    is_configured: false,
  });

  const fetchSmtpConfig = async () => {
    setSmtpLoading(true);
    setSmtpError('');
    try {
      const res = await smtpAPI.get();
      if (res?.data?.data) {
        const d = res.data.data;
        setSmtpForm({
          smtp_host: d.smtp_host || 'smtp.gmail.com',
          smtp_port: d.smtp_port || 587,
          smtp_user: d.smtp_user || '',
          smtp_password: d.has_password ? '••••••••••••••••' : '',
          smtp_from: d.smtp_from || '',
          smtp_encryption: d.smtp_encryption || 'TLS',
          has_password: d.has_password,
          is_configured: d.is_configured,
        });
        if (user?.email && !testRecipient) {
          setTestRecipient(user.email);
        }
      }
    } catch (err) {
      console.error('Failed to load SMTP config:', err);
      setSmtpError(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to load SMTP configuration');
    } finally {
      setSmtpLoading(false);
    }
  };

  const fetchEmailQueue = async () => {
    setQueueLoading(true);
    try {
      const res = await smtpAPI.getQueue(30);
      if (res?.data?.data) {
        setEmailQueue(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load email queue:', err);
    } finally {
      setQueueLoading(false);
    }
  };

  const openSmtpModal = async () => {
    setShowSmtpModal(true);
    setSmtpTab('settings');
    setSmtpError('');
    setSmtpSuccess('');
    setTestResult(null);
    await fetchSmtpConfig();
  };

  const handleSaveSmtp = async (e) => {
    e.preventDefault();
    setSmtpSaving(true);
    setSmtpError('');
    setSmtpSuccess('');
    setTestResult(null);
    try {
      const payload = {
        smtp_host: smtpForm.smtp_host,
        smtp_port: parseInt(smtpForm.smtp_port, 10),
        smtp_user: smtpForm.smtp_user.trim(),
        smtp_from: smtpForm.smtp_from.trim(),
        smtp_encryption: smtpForm.smtp_encryption,
      };
      if (smtpForm.smtp_password && !smtpForm.smtp_password.startsWith('•')) {
        payload.smtp_password = smtpForm.smtp_password;
      }
      const res = await smtpAPI.update(payload);
      setSmtpSuccess(res?.data?.message || 'SMTP settings saved successfully!');
      if (res?.data?.data) {
        const d = res.data.data;
        setSmtpForm(prev => ({
          ...prev,
          ...d,
          smtp_password: d.has_password ? '••••••••••••••••' : '',
        }));
      }
    } catch (err) {
      console.error('Failed to save SMTP settings:', err);
      setSmtpError(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to save SMTP settings');
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    setTestLoading(true);
    setTestResult(null);
    setSmtpError('');
    try {
      const payload = {
        recipient: testRecipient.trim() || undefined,
        smtp_host: smtpForm.smtp_host,
        smtp_port: parseInt(smtpForm.smtp_port, 10),
        smtp_user: smtpForm.smtp_user.trim(),
        smtp_from: smtpForm.smtp_from.trim(),
        smtp_encryption: smtpForm.smtp_encryption,
      };
      if (smtpForm.smtp_password && !smtpForm.smtp_password.startsWith('•')) {
        payload.smtp_password = smtpForm.smtp_password;
      }
      const res = await smtpAPI.test(payload);
      setTestResult({ success: true, message: res?.data?.message || 'Test email sent successfully!' });
    } catch (err) {
      console.error('SMTP test error:', err);
      setTestResult({
        success: false,
        message: err?.response?.data?.detail || err?.response?.data?.message || 'Connection or authentication failed.',
      });
    } finally {
      setTestLoading(false);
    }
  };

  const handleRetryQueue = async () => {
    setQueueRetrying(true);
    try {
      const res = await smtpAPI.retryPending();
      alert(res?.data?.message || 'Processed pending emails.');
      await fetchEmailQueue();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to retry queue');
    } finally {
      setQueueRetrying(false);
    }
  };

  // ── Environment Variables State & Handlers ────────────────────────────────
  const [envVars, setEnvVars] = useState([]);
  const [envLoading, setEnvLoading] = useState(false);
  const [envError, setEnvError] = useState('');
  const [envSuccess, setEnvSuccess] = useState('');
  const [editingEnvKey, setEditingEnvKey] = useState('');
  const [editingEnvVal, setEditingEnvVal] = useState('');
  const [adminAuthPassword, setAdminAuthPassword] = useState('');
  const [envSaving, setEnvSaving] = useState(false);
  const [dbTesting, setDbTesting] = useState(false);
  const [dbTestResult, setDbTestResult] = useState(null);
  const [envSearchTerm, setEnvSearchTerm] = useState('');

  const fetchEnvVars = async () => {
    setEnvLoading(true);
    setEnvError('');
    try {
      const res = await envAPI.list();
      if (res?.data?.data) {
        setEnvVars(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load env vars:', err);
      setEnvError(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to load environment variables');
    } finally {
      setEnvLoading(false);
    }
  };

  const handleStartEditEnv = (item) => {
    setEditingEnvKey(item.key);
    setEditingEnvVal('');
    setAdminAuthPassword('');
    setDbTestResult(null);
    setEnvError('');
    setEnvSuccess('');
  };

  const handleTestDatabaseUrl = async (urlToTest) => {
    if (!urlToTest.trim()) {
      alert('Please enter a Database connection string to test.');
      return;
    }
    setDbTesting(true);
    setDbTestResult(null);
    try {
      const res = await envAPI.testDb(urlToTest.trim());
      setDbTestResult({ success: true, message: res?.data?.message || 'Database connection successful!' });
    } catch (err) {
      setDbTestResult({
        success: false,
        message: err?.response?.data?.detail || err?.response?.data?.message || 'Failed to connect to database.',
      });
    } finally {
      setDbTesting(false);
    }
  };

  const handleSaveEnvVariable = async (e) => {
    e.preventDefault();
    if (!editingEnvKey) return;
    if (!editingEnvVal.trim()) {
      setEnvError('Value cannot be empty.');
      return;
    }
    if (!adminAuthPassword.trim()) {
      setEnvError('Your current Admin login password is required to authorize this modification.');
      return;
    }

    setEnvSaving(true);
    setEnvError('');
    setEnvSuccess('');
    try {
      const res = await envAPI.update(editingEnvKey, editingEnvVal.trim(), adminAuthPassword.trim());
      setEnvSuccess(res?.data?.message || `Variable '${editingEnvKey}' updated. Security confirmation email sent.`);
      setEditingEnvKey('');
      setEditingEnvVal('');
      setAdminAuthPassword('');
      setDbTestResult(null);
      await fetchEnvVars();
    } catch (err) {
      console.error('Failed to update env var:', err);
      setEnvError(err?.response?.data?.detail || err?.response?.data?.message || 'Failed to update environment variable');
    } finally {
      setEnvSaving(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, sortBy, sortDir]);

  useEffect(() => {
    fetchUsers(page, debouncedSearch, sortBy, sortDir);
  }, [page, debouncedSearch, sortBy, sortDir]);

  useEffect(() => {
    fetchManagers();
  }, []);

  const fetchUsers = async (pageNum = page, search = debouncedSearch, sort = sortBy, dir = sortDir) => {
    setLoading(true);
    try {
      const response = await usersAPI.list({
        page: pageNum,
        size: 10,
        search: search || undefined,
        sort_by: sort,
        sort_dir: dir,
      });
      const newUsers = response?.data?.data?.users || [];
      setUsers(newUsers);

      if (response?.data?.data) {
        setPaginationInfo({
          page: response.data.data.page,
          size: response.data.data.size,
          total_records: response.data.data.total,
          total_pages: response.data.data.total_pages
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      const response = await usersAPI.getManagers();
      setManagers(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (error) {
      console.error('Error fetching managers:', error);
      setManagers([]);
    }
  };

  const renderPagination = () => {
    const { page: currPage, size: currSize, total_records, total_pages } = paginationInfo;
    if (total_pages <= 1) return null;

    const startRecord = (currPage - 1) * currSize + 1;
    const endRecord = Math.min(currPage * currSize, total_records);

    // Generate page numbers to display
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(total_pages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="premium-pagination">
        <div className="premium-pagination-info">
          Showing <span style={{ fontWeight: 600 }}>{startRecord}</span> to{' '}
          <span style={{ fontWeight: 600 }}>{endRecord}</span> of{' '}
          <span style={{ fontWeight: 600 }}>{total_records}</span> entries
        </div>
        <div className="premium-pagination-controls">
          <button
            className="premium-pagination-btn"
            onClick={() => setPage(1)}
            disabled={currPage === 1}
            title="First Page"
          >
            &lt;&lt;
          </button>
          <button
            className="premium-pagination-btn"
            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            disabled={currPage === 1}
            title="Previous Page"
          >
            &lt;
          </button>
          
          {startPage > 1 && (
            <>
              <button className="premium-pagination-btn" onClick={() => setPage(1)}>1</button>
              {startPage > 2 && <span style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>}
            </>
          )}

          {pageNumbers.map(num => (
            <button
              key={num}
              className={`premium-pagination-btn ${currPage === num ? 'active' : ''}`}
              onClick={() => setPage(num)}
            >
              {num}
            </button>
          ))}

          {endPage < total_pages && (
            <>
              {endPage < total_pages - 1 && <span style={{ padding: '0 4px', color: 'var(--text-secondary)' }}>...</span>}
              <button className="premium-pagination-btn" onClick={() => setPage(total_pages)}>{total_pages}</button>
            </>
          )}

          <button
            className="premium-pagination-btn"
            onClick={() => setPage(prev => Math.min(prev + 1, total_pages))}
            disabled={currPage === total_pages}
            title="Next Page"
          >
            &gt;
          </button>
          <button
            className="premium-pagination-btn"
            onClick={() => setPage(total_pages)}
            disabled={currPage === total_pages}
            title="Last Page"
          >
            &gt;&gt;
          </button>
        </div>
      </div>
    );
  };

  const resetForm = () => {
    setSelectedUser(null);
    setFormData(EMPTY_FORM);
  };

  const openCreateModal = async () => {
    resetForm();
    await fetchManagers(); // Fetch managers before showing modal
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await usersAPI.update(selectedUser.id, {
          name: formData.name,
          email: formData.email,
          username: formData.username,
          status: formData.status,
          parent_id: formData.parent_id === '' ? null : parseInt(formData.parent_id, 10),
          timezone: formData.timezone,
        });
        await usersAPI.updateRoles(selectedUser.id, formData.roles);
      } else {
        await usersAPI.create({
          ...formData,
          parent_id: formData.parent_id === '' ? null : parseInt(formData.parent_id, 10),
        });
      }
      setShowModal(false);
      resetForm();
      fetchUsers(1, debouncedSearch, sortBy, sortDir, true);
    } catch (error) {
      console.error('Error saving user:', error);
      alert(error?.response?.data?.message || 'Failed to save user. Check email, username, and manager details.');
    }
  };

  const handleEdit = async (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      username: user.username || '',
      password: '',
      parent_id: user.parent_id || '',
      roles: user.roles.map((role) => role.value),
      status: user.status,
      timezone: user.timezone || 'Asia/Kolkata',
    });
    await fetchManagers(); // Fetch managers before showing modal
    setShowModal(true);
  };

  const handleDelete = async (userId, hardDelete = false) => {
    const msg = hardDelete
      ? 'Are you sure you want to mark this user for PERMANENT deletion? All data will be purged in 30 days.'
      : 'Are you sure you want to deactivate this user?';

    if (!window.confirm(msg)) return;

    try {
      await usersAPI.delete(userId, { params: { hard_delete: hardDelete } });
      fetchUsers(1, debouncedSearch, sortBy, sortDir, true);
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error?.response?.data?.detail || 'Action failed');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await usersAPI.update(user.id, { status: !user.status });
      fetchUsers(1, debouncedSearch, sortBy, sortDir, true);
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update status');
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('File size exceeds 2MB limit.');
      return;
    }

    setLoading(true);
    setShowBulkModal(false);
    try {
      const response = await importAPI.users(file);
      const payload = response?.data?.data || {};
      alert(`Bulk Import Complete!\nSuccessfully created: ${payload.success_count}\nFailed: ${payload.failure_count}`);
      fetchUsers(1, debouncedSearch, sortBy, sortDir, true);
    } catch (error) {
      alert(error?.response?.data?.message || 'User import failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role) => {
    setFormData((prev) => ({
      ...prev,
      roles: prev.roles.includes(role)
        ? prev.roles.filter((item) => item !== role)
        : [...prev.roles, role],
    }));
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'Admin':
        return 'role-badge admin';
      case 'Manager':
        return 'role-badge manager';
      default:
        return 'role-badge member';
    }
  };

  if (loading && page === 1) {
    return <div className="loading-container"><div className="spinner"></div></div>;
  }

  return (
    <div className="page-wrapper">
      <header className="header">
        <div className="header-title">
          <h2>User Provisioning</h2>
          <p>Manage users and role assignments</p>
        </div>
        <div className="header-actions">
          <div className="search-container">
            <Search className="search-icon" size={18} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="search-container" style={{ minWidth: '180px' }}>
            <select
              className="form-input"
              style={{ paddingLeft: '10px' }}
              value={`${sortBy}-${sortDir}`}
              onChange={(e) => {
                const [newSortBy, newSortDir] = e.target.value.split('-');
                setSortBy(newSortBy);
                setSortDir(newSortDir);
              }}
            >
              <option value="name-asc">Alphabetical (A-Z)</option>
              <option value="name-desc">Alphabetical (Z-A)</option>
              <option value="created_at-desc">Date Added (Newest)</option>
              <option value="created_at-asc">Date Added (Oldest)</option>
              <option value="active-desc">Most Active</option>
            </select>
          </div>
          <button className="btn btn-secondary" onClick={openDomainModal} title="Configure allowed email domains">
            <Globe size={18} /> Allowed Domains
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/system-settings')} title="Configure SMTP Email, Database URL, and Environment Variables">
            <Server size={18} /> System Settings
          </button>
          <button className="btn btn-secondary" onClick={() => setShowBulkModal(true)}>
            <Plus size={18} /> Bulk Import (CSV)
          </button>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <UserPlus size={18} /> Add User
          </button>
        </div>
      </header>

      <div className="page-content">
        <div className="card" style={{ maxWidth: '100%', overflow: 'visible' }}>
          <div className="card-header">
            <h3 className="card-title">All Users ({paginationInfo.total_records})</h3>
          </div>
          
          {renderPagination()}

          <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
            <table className="table" style={{ width: '100%', minWidth: '900px' }}>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>User</th>
                  <th style={{ width: '30%' }}>Email</th>
                  <th style={{ width: '15%' }}>Roles</th>
                  <th style={{ width: '15%' }}>Manager</th>
                  <th style={{ width: '8%' }}>Status</th>
                  <th style={{ width: '7%' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="avatar">{user.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}</div>
                          <div style={{ minWidth: 0, position: 'relative', flex: 1 }}>
                            <div
                              className="font-medium"
                              style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'default' }}
                              onMouseEnter={(e) => {
                                if (user.username) {
                                  setHoveredUserId(user.id);
                                  setTooltipPos({ x: e.clientX, y: e.clientY });
                                }
                              }}
                              onMouseLeave={() => setHoveredUserId(null)}
                            >
                              {user.name}
                            </div>
                            <div className="text-xs text-muted">ID: {user.id}</div>
                          </div>
                        </div>
                        {hoveredUserId === user.id && user.username && (
                          <div
                            style={{
                              position: 'fixed',
                              left: tooltipPos.x + 12,
                              top: tooltipPos.y - 36,
                              background: 'var(--bg-card, #1e293b)',
                              color: 'var(--text-on-dark, #f1f5f9)',
                              padding: '5px 10px',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 500,
                              pointerEvents: 'none',
                              zIndex: 9999,
                              whiteSpace: 'nowrap',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                            }}
                          >
                            @{user.username}
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={user.email}>
                          {user.email}
                        </div>
                      </td>
                      <td>
                        <div className="flex gap-1 flex-wrap">
                          {user.roles.map((role) => (
                            <span key={role.id} className={getRoleBadgeClass(role.value)} style={{ fontSize: '11px', padding: '3px 8px', whiteSpace: 'nowrap' }}>
                              {role.value}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td style={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={user.manager_name || '-'}>
                          {user.manager_name || '-'}
                        </div>
                      </td>
                      <td>
                        <span className={`badge badge-${user.status ? 'active' : 'inactive'}`}>
                          {user.status ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <button
                            className="btn btn-icon"
                            style={{ padding: '4px', color: user.status ? 'var(--warning)' : 'var(--success)' }}
                            onClick={() => handleToggleStatus(user)}
                            title={user.status ? 'Deactivate' : 'Activate'}
                          >
                            <Shield size={16} />
                          </button>
                          <button className="btn btn-icon" style={{ padding: '4px' }} onClick={() => handleEdit(user)} title="Edit">
                            <Edit2 size={16} />
                          </button>
                          <button
                            className="btn btn-icon"
                            style={{ padding: '4px', color: '#ef4444' }}
                            onClick={() => handleDelete(user.id, true)}
                            title="Permanent Delete (30 Day Queue)"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderPagination()}
        </div>
      </div>

      {showModal && (
        <div className="premium-drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="premium-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>{selectedUser ? 'Edit User' : 'Create New User'}</h3>
              <button className="btn btn-icon" onClick={() => setShowModal(false)}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <div className="drawer-body">
                <div className="form-group">
                  <label className="form-label">Full Name</label>
                  <input type="text" className="form-input" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label">Email Address</label>
                    <button
                      type="button"
                      onClick={openDomainModal}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
                    >
                      View Allowed Domains
                    </button>
                  </div>
                  <input type="email" className="form-input" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required />
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '4px' }}>
                    Must match an authorized organization domain.
                  </span>
                </div>
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input type="text" className="form-input" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
                </div>

                <div className="form-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', marginBottom: '24px' }}>
                  <div>
                    <label className="form-label" style={{ marginBottom: '4px' }}>Account Status</label>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{formData.status ? 'Active - Can Log In' : 'Deactivated'}</span>
                  </div>
                  <button type="button" onClick={() => setFormData({ ...formData, status: !formData.status })} style={{ width: '48px', height: '24px', borderRadius: '12px', background: formData.status ? 'var(--primary)' : '#cbd5e1', position: 'relative', border: 'none', cursor: 'pointer' }}>
                    <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: formData.status ? '27px' : '3px', transition: 'all 0.2s' }} />
                  </button>
                </div>

                {!selectedUser && (
                  <div className="form-group">
                    <label className="form-label">Initial Password</label>
                    <input type="password" className="form-input" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} required />
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">User's Timezone</label>
                  <select className="form-select" value={formData.timezone} onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}>
                    {TIMEZONES.map((tz) => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Reporting Manager</label>
                  <select className="form-select" value={formData.parent_id} onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}>
                    <option value="">No Manager</option>
                    {managers.map((manager) => <option key={manager.id} value={manager.id}>{manager.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Access Roles</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {['Admin', 'Manager', 'Member'].map((role) => (
                      <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox" checked={formData.roles.includes(role)} onChange={() => handleRoleChange(role)} />
                        <span>{role}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="drawer-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{selectedUser ? 'Save Changes' : 'Create Account'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showBulkModal && (
        <div className="premium-drawer-overlay" onClick={() => setShowBulkModal(false)}>
          <div className="premium-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3>Bulk Import</h3>
              <button className="btn btn-icon" onClick={() => setShowBulkModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="drawer-body">
              <input type="file" accept=".csv" onChange={handleFileChange} />
            </div>
          </div>
        </div>
      )}

      {showDomainModal && (
        <div className="premium-drawer-overlay" onClick={() => setShowDomainModal(false)}>
          <div className="premium-drawer" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Globe size={20} style={{ color: 'var(--primary)' }} />
                  Allowed Email Domains
                </h3>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Only users with these email domains can register or be provisioned.
                </p>
              </div>
              <button className="btn btn-icon" onClick={() => setShowDomainModal(false)}>
                <X size={24} />
              </button>
            </div>

            <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {domainSuccess && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '6px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.3)',
                  color: 'var(--success)',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Check size={16} /> {domainSuccess}
                </div>
              )}

              {domainError && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '6px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: 'var(--error)',
                  fontSize: '13px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <AlertCircle size={16} /> {domainError}
                </div>
              )}

              {/* Add Domain Form */}
              <form onSubmit={handleAddDomain} style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)' }}>
                <label className="form-label" style={{ marginBottom: '8px', fontWeight: 600 }}>
                  Add Authorized Domain
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. tickmarks.net or @company.com"
                    value={newDomainInput}
                    onChange={(e) => setNewDomainInput(e.target.value)}
                    disabled={domainLoading}
                    style={{ flex: 1 }}
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={domainLoading || !newDomainInput.trim()}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {domainLoading ? 'Adding...' : '+ Add Domain'}
                  </button>
                </div>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginTop: '6px' }}>
                  Domain format can be entered with or without '@' (e.g., example.com or @example.com).
                </span>
              </form>

              {/* Current Domains List */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-primary)' }}>
                  Active Allowed Domains ({allowedDomains.length})
                </h4>

                {domainLoading && allowedDomains.length === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    Loading domains...
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {allowedDomains.map((d) => (
                      <div
                        key={d.id || d.domain}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 14px',
                          background: 'white',
                          border: '1px solid var(--border)',
                          borderRadius: '8px',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span
                            style={{
                              background: 'rgba(13, 148, 136, 0.1)',
                              color: 'var(--primary)',
                              padding: '4px 10px',
                              borderRadius: '6px',
                              fontFamily: 'monospace',
                              fontWeight: 600,
                              fontSize: '13px',
                            }}
                          >
                            @{d.domain}
                          </span>
                          {d.created_at && (
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                              Added: {new Date(d.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>

                        <button
                          type="button"
                          className="btn btn-icon"
                          style={{
                            color: allowedDomains.length <= 1 ? '#cbd5e1' : '#ef4444',
                            cursor: allowedDomains.length <= 1 ? 'not-allowed' : 'pointer',
                            padding: '6px',
                          }}
                          onClick={() => allowedDomains.length > 1 && handleDeleteDomain(d.id, d.domain)}
                          disabled={allowedDomains.length <= 1 || domainLoading}
                          title={allowedDomains.length <= 1 ? 'Cannot delete the only domain' : 'Delete Domain'}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={() => setShowDomainModal(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── SMTP Email Delivery Drawer ────────────────────────────────────────── */}
      {showSmtpModal && (
        <div className="premium-drawer-overlay" onClick={() => setShowSmtpModal(false)}>
          <div
            className="premium-drawer"
            style={{ maxWidth: '580px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="drawer-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ background: 'rgba(13, 148, 136, 0.1)', padding: '8px', borderRadius: '8px', color: 'var(--primary)' }}>
                  <Mail size={22} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Email &amp; SMTP Settings</h3>
                  <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    Configure outbound email delivery for verification, OTP, and timesheet alerts
                  </p>
                </div>
              </div>
              <button className="btn btn-icon" onClick={() => setShowSmtpModal(false)}>
                <X size={20} />
              </button>
            </div>

            {/* Tab switch */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 20px', gap: '4px' }}>
              <button
                type="button"
                style={{
                  padding: '12px 14px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: smtpTab === 'settings' ? 'var(--primary)' : '#64748b',
                  borderBottom: smtpTab === 'settings' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
                onClick={() => setSmtpTab('settings')}
              >
                SMTP Configuration
              </button>
              <button
                type="button"
                style={{
                  padding: '12px 14px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: smtpTab === 'env' ? 'var(--primary)' : '#64748b',
                  borderBottom: smtpTab === 'env' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
                onClick={() => { setSmtpTab('env'); fetchEnvVars(); }}
              >
                Environment Variables
              </button>
              <button
                type="button"
                style={{
                  padding: '12px 14px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                  color: smtpTab === 'queue' ? 'var(--primary)' : '#64748b',
                  borderBottom: smtpTab === 'queue' ? '2px solid var(--primary)' : '2px solid transparent',
                }}
                onClick={() => { setSmtpTab('queue'); fetchEmailQueue(); }}
              >
                Outbox Queue
              </button>
            </div>

            <div className="drawer-body" style={{ padding: '20px', overflowY: 'auto' }}>
              {smtpError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <AlertCircle size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>{smtpError}</div>
                </div>
              )}

              {smtpSuccess && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '12px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle2 size={18} />
                  <div>{smtpSuccess}</div>
                </div>
              )}

              {smtpTab === 'settings' && (
                <>
                  {/* Status Banner */}
                  <div style={{
                    background: smtpForm.is_configured ? 'rgba(16, 185, 129, 0.08)' : 'rgba(245, 158, 11, 0.08)',
                    border: `1px solid ${smtpForm.is_configured ? 'rgba(16, 185, 129, 0.3)' : 'rgba(245, 158, 11, 0.3)'}`,
                    borderRadius: '8px',
                    padding: '14px',
                    marginBottom: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: smtpForm.is_configured ? '#10b981' : '#f59e0b',
                      }} />
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: smtpForm.is_configured ? '#065f46' : '#92400e' }}>
                          {smtpForm.is_configured ? 'SMTP is Configured & Active' : 'SMTP Credentials Not Configured'}
                        </div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          {smtpForm.is_configured ? `Sending via ${smtpForm.smtp_user} (${smtpForm.smtp_host})` : 'Emails will remain queued until credentials are configured.'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowSmtpHelp(!showSmtpHelp)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}
                    >
                      <HelpCircle size={15} /> {showSmtpHelp ? 'Hide Guide' : 'Gmail 2FA Guide'}
                    </button>
                  </div>

                  {/* Gmail 2FA & App Password Guide */}
                  {showSmtpHelp && (
                    <div style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '16px', marginBottom: '18px', fontSize: '12px', color: '#334155' }}>
                      <div style={{ fontWeight: 600, marginBottom: '8px', color: '#0f172a', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Key size={14} color="var(--primary)" /> How to set up Gmail App Password (2-Factor Authentication required):
                      </div>
                      <ol style={{ paddingLeft: '18px', margin: 0, lineHeight: 1.6 }}>
                        <li>Log in to your Google Account (e.g. <strong>timesheet@tickmarks.net</strong> or your Gmail).</li>
                        <li>Go to <strong>Manage your Google Account &gt; Security</strong>.</li>
                        <li>Ensure <strong>2-Step Verification</strong> is switched <strong>ON</strong>.</li>
                        <li>Search for or scroll to <strong>App Passwords</strong> (or search "App passwords" in the top bar).</li>
                        <li>Create a new app name (e.g., <em>TimeTracker SMTP</em>) and click <strong>Create</strong>.</li>
                        <li>Google will display a <strong>16-character code</strong> (e.g., <code>abcd efgh ijkl mnop</code>).</li>
                        <li>Paste that 16-character code into the <strong>App Password</strong> field below and click <strong>Save Configuration</strong>.</li>
                      </ol>
                    </div>
                  )}

                  {/* Configuration Form */}
                  <form onSubmit={handleSaveSmtp}>
                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>SMTP Host</label>
                        <input
                          type="text"
                          className="form-control"
                          value={smtpForm.smtp_host}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_host: e.target.value })}
                          placeholder="smtp.gmail.com"
                          required
                        />
                      </div>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Port</label>
                        <input
                          type="number"
                          className="form-control"
                          value={smtpForm.smtp_port}
                          onChange={(e) => {
                            const p = parseInt(e.target.value, 10);
                            setSmtpForm({
                              ...smtpForm,
                              smtp_port: e.target.value,
                              smtp_encryption: p === 465 ? 'SSL' : 'TLS',
                            });
                          }}
                          placeholder="587"
                          required
                        />
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Encryption</label>
                        <select
                          className="form-control"
                          value={smtpForm.smtp_encryption}
                          onChange={(e) => {
                            const enc = e.target.value;
                            setSmtpForm({
                              ...smtpForm,
                              smtp_encryption: enc,
                              smtp_port: enc === 'SSL' ? 465 : 587,
                            });
                          }}
                        >
                          <option value="TLS">STARTTLS (Port 587)</option>
                          <option value="SSL">SSL / TLS (Port 465)</option>
                        </select>
                      </div>

                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>Sender Email / Username</label>
                        <input
                          type="email"
                          className="form-control"
                          value={smtpForm.smtp_user}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_user: e.target.value })}
                          placeholder="e.g. timesheet@tickmarks.net"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: '14px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>
                        SMTP / Google App Password (16 characters)
                      </label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showSmtpPassword ? 'text' : 'password'}
                          className="form-control"
                          style={{ paddingRight: '40px' }}
                          value={smtpForm.smtp_password}
                          onChange={(e) => setSmtpForm({ ...smtpForm, smtp_password: e.target.value })}
                          placeholder="Enter 16-character Google App Password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                          style={{
                            position: 'absolute',
                            right: '10px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#94a3b8',
                            padding: '4px',
                          }}
                        >
                          {showSmtpPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', display: 'block' }}>
                        {smtpForm.has_password
                          ? 'A password is currently saved. Leave as •••••••• to keep it, or type a new App Password to update.'
                          : 'Paste the 16-character App Password generated from Google Account > Security > App passwords.'}
                      </span>
                    </div>

                    <div className="form-group" style={{ marginBottom: '18px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, marginBottom: '4px', display: 'block' }}>From Header (Display Name)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={smtpForm.smtp_from}
                        onChange={(e) => setSmtpForm({ ...smtpForm, smtp_from: e.target.value })}
                        placeholder="Tickmarks TimeTracker <timesheet@tickmarks.net>"
                      />
                    </div>

                    <div style={{ display: 'flex', gap: '10px', marginBottom: '24px' }}>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        disabled={smtpSaving || smtpLoading}
                      >
                        {smtpSaving ? 'Saving...' : 'Save Configuration'}
                      </button>
                    </div>
                  </form>

                  {/* Test Connection Box */}
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Send size={15} color="var(--primary)" /> Send Test Email
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="email"
                        className="form-control"
                        value={testRecipient}
                        onChange={(e) => setTestRecipient(e.target.value)}
                        placeholder="Recipient email address"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleTestSmtp}
                        disabled={testLoading || !smtpForm.smtp_user}
                      >
                        {testLoading ? 'Testing...' : 'Test Connection'}
                      </button>
                    </div>

                    {testResult && (
                      <div style={{
                        marginTop: '12px',
                        padding: '10px 14px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        background: testResult.success ? '#f0fdf4' : '#fef2f2',
                        border: `1px solid ${testResult.success ? '#bbf7d0' : '#fecaca'}`,
                        color: testResult.success ? '#15803d' : '#b91c1c',
                      }}>
                        <strong>{testResult.success ? 'Success: ' : 'Test Failed: '}</strong>
                        {testResult.message}
                      </div>
                    )}
                  </div>
                </>
              )}

              {smtpTab === 'env' && (
                <div>
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '12px', color: '#166534', lineHeight: 1.5 }}>
                    <strong>Vercel &amp; Render Style Environment Manager:</strong> Manage PostgreSQL database URL, hosts, secrets, and API keys. Any modification requires your <strong>Admin Login Password</strong> and triggers an instant security alert email to your address.
                  </div>

                  {envError && (
                    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '10px 14px', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>
                      {envError}
                    </div>
                  )}

                  {envSuccess && (
                    <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d', padding: '10px 14px', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>
                      {envSuccess}
                    </div>
                  )}

                  {editingEnvKey && (
                    <div style={{ background: '#f8fafc', border: '2px solid var(--primary)', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>
                          Modify Variable: <code>{editingEnvKey}</code>
                        </span>
                        <button
                          type="button"
                          className="btn btn-icon"
                          style={{ padding: '2px' }}
                          onClick={() => { setEditingEnvKey(''); setDbTestResult(null); }}
                        >
                          <X size={16} />
                        </button>
                      </div>

                      <form onSubmit={handleSaveEnvVariable}>
                        <div className="form-group" style={{ marginBottom: '12px' }}>
                          <label style={{ fontSize: '11px', fontWeight: 600, color: '#475569', display: 'block', marginBottom: '4px' }}>
                            New Value
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            value={editingEnvVal}
                            onChange={(e) => setEditingEnvVal(e.target.value)}
                            placeholder={editingEnvKey === 'DATABASE_URL' ? 'postgresql://postgres:password@localhost:5432/timesheet' : 'Enter new value'}
                            required
                          />
                        </div>

                        {editingEnvKey === 'DATABASE_URL' && (
                          <div style={{ marginBottom: '12px' }}>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '5px 10px' }}
                              onClick={() => handleTestDatabaseUrl(editingEnvVal)}
                              disabled={dbTesting || !editingEnvVal.trim()}
                            >
                              <Server size={13} style={{ marginRight: '4px' }} />
                              {dbTesting ? 'Testing PostgreSQL Connection...' : 'Test Database Connection'}
                            </button>
                            {dbTestResult && (
                              <div style={{
                                marginTop: '6px',
                                padding: '6px 10px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                background: dbTestResult.success ? '#dcfce7' : '#fee2e2',
                                color: dbTestResult.success ? '#15803d' : '#b91c1c',
                              }}>
                                {dbTestResult.message}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="form-group" style={{ marginBottom: '14px' }}>
                          <label style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626', display: 'block', marginBottom: '4px' }}>
                            <Lock size={12} style={{ display: 'inline', marginRight: '4px' }} />
                            Admin Password Confirmation (Required)
                          </label>
                          <input
                            type="password"
                            className="form-control"
                            value={adminAuthPassword}
                            onChange={(e) => setAdminAuthPassword(e.target.value)}
                            placeholder="Enter your current login password to authorize"
                            required
                          />
                        </div>

                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ flex: 1, fontSize: '12px' }}
                            disabled={envSaving || !editingEnvVal.trim() || !adminAuthPassword.trim()}
                          >
                            {envSaving ? 'Authorizing & Saving...' : 'Save & Authorize Change'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: '12px' }}
                            onClick={() => { setEditingEnvKey(''); setDbTestResult(null); }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                  {/* Variables list */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <input
                      type="text"
                      className="form-control"
                      style={{ maxWidth: '240px', fontSize: '12px', padding: '6px 10px' }}
                      value={envSearchTerm}
                      onChange={(e) => setEnvSearchTerm(e.target.value)}
                      placeholder="Filter variables..."
                    />
                    <button
                      type="button"
                      className="btn btn-secondary"
                      style={{ fontSize: '11px', padding: '6px 10px' }}
                      onClick={fetchEnvVars}
                      disabled={envLoading}
                    >
                      <RefreshCw size={13} className={envLoading ? 'spin' : ''} /> Refresh
                    </button>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {envVars
                      .filter((v) => !envSearchTerm || v.key.toLowerCase().includes(envSearchTerm.toLowerCase()))
                      .map((item) => (
                        <div
                          key={item.key}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '10px 12px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: '12px', color: '#0f172a' }}>
                                {item.key}
                              </span>
                              <span style={{ fontSize: '10px', background: '#f1f5f9', color: '#475569', padding: '1px 5px', borderRadius: '4px' }}>
                                {item.source}
                              </span>
                            </div>
                            <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#64748b', marginTop: '2px', wordBreak: 'break-all' }}>
                              {item.masked_value || '<Not Set>'}
                            </div>
                            {item.description && (
                              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>
                                {item.description}
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ fontSize: '11px', padding: '4px 8px', flexShrink: 0 }}
                            onClick={() => handleStartEditEnv(item)}
                          >
                            <Edit2 size={12} style={{ marginRight: '3px' }} /> Edit
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {smtpTab === 'queue' && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                    <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                      Recent Queued Emails ({emailQueue.length})
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '6px 10px' }}
                        onClick={fetchEmailQueue}
                        disabled={queueLoading}
                      >
                        <RefreshCw size={14} className={queueLoading ? 'spin' : ''} /> Refresh
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ fontSize: '12px', padding: '6px 12px' }}
                        onClick={handleRetryQueue}
                        disabled={queueRetrying}
                      >
                        {queueRetrying ? 'Retrying...' : 'Retry Pending / Failed'}
                      </button>
                    </div>
                  </div>

                  {queueLoading && emailQueue.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>Loading outbox...</div>
                  ) : emailQueue.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', background: '#f8fafc', borderRadius: '8px' }}>
                      No emails in outbox queue.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {emailQueue.map((item) => (
                        <div
                          key={item.id}
                          style={{
                            background: '#ffffff',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '12px 14px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{item.recipient}</span>
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: '2px 8px',
                                borderRadius: '12px',
                                background: item.status === 'SENT' ? '#dcfce7' : item.status === 'PENDING' ? '#fef3c7' : '#fee2e2',
                                color: item.status === 'SENT' ? '#15803d' : item.status === 'PENDING' ? '#b45309' : '#b91c1c',
                              }}
                            >
                              {item.status}
                            </span>
                          </div>
                          <div style={{ fontSize: '12px', color: '#475569' }}>{item.subject}</div>
                          {item.error_message && (
                            <div style={{ fontSize: '11px', color: '#b91c1c', background: '#fef2f2', padding: '4px 8px', borderRadius: '4px', marginTop: '4px' }}>
                              Error: {item.error_message}
                            </div>
                          )}
                          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                            {item.sent_at ? `Sent: ${new Date(item.sent_at).toLocaleString()}` : `Queued: ${new Date(item.created_at).toLocaleString()}`}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="drawer-footer">
              <button
                type="button"
                className="btn btn-secondary"
                style={{ width: '100%' }}
                onClick={() => setShowSmtpModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProvisioning;
